import {useEffect, useRef, useState} from "react";

export const PING_PONG_END_PAUSE_MS = 1200;
export const PING_PONG_SPEED_PX_PER_SEC = 32;
export const PING_PONG_START_DELAY_MS = 800;

interface IParams {
    enabled?: boolean;
}

export interface IPingPongMotion {
    direction: -1 | 1;
    position: number;
    remainingPauseMs: number;
}

interface IAnimationRuntime {
    frameId: number;
    isInView: boolean;
    lastFrameTime: number;
    motion: IPingPongMotion;
    overflowPx: number;
    prefersReducedMotion: boolean;
}

export const getOverflowPx = (scrollWidth: number, clientWidth: number) => Math.max(0, Math.round(scrollWidth - clientWidth));

export const createPingPongMotion = (): IPingPongMotion => ({
    direction: 1,
    position: 0,
    remainingPauseMs: PING_PONG_START_DELAY_MS
});

export const advancePingPong = (motion: IPingPongMotion, deltaMs: number, overflowPx: number): IPingPongMotion => {
    if (overflowPx <= 0) {
        return {direction: 1, position: 0, remainingPauseMs: 0};
    }

    const {direction} = motion;
    let {position, remainingPauseMs} = motion;
    let moveMs = deltaMs;

    if (remainingPauseMs > 0) {
        if (moveMs <= remainingPauseMs) {
            return {direction, position, remainingPauseMs: remainingPauseMs - moveMs};
        }

        moveMs -= remainingPauseMs;
        remainingPauseMs = 0;
    }

    position += (direction * PING_PONG_SPEED_PX_PER_SEC * moveMs) / 1000;

    if (position >= overflowPx) {
        return {direction: -1, position: overflowPx, remainingPauseMs: PING_PONG_END_PAUSE_MS};
    }

    if (position <= 0) {
        return {direction: 1, position: 0, remainingPauseMs: PING_PONG_END_PAUSE_MS};
    }

    return {direction, position, remainingPauseMs: 0};
};

const prefersReducedMotion = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const usePingPongOverflowText = (params?: IParams) => {
    const enabled = params?.enabled ?? true;
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [isReducedMotion, setIsReducedMotion] = useState(prefersReducedMotion);
    const [isInView, setIsInView] = useState(true);

    useEffect(() => {
        if (!enabled) {
            setIsOverflowing(false);
            return;
        }

        const container = containerRef.current;
        const content = contentRef.current;

        if (!container || !content) {
            return;
        }

        if (typeof ResizeObserver === "undefined" || typeof IntersectionObserver === "undefined") {
            setIsOverflowing(getOverflowPx(content.scrollWidth, container.clientWidth) > 0);
            setIsReducedMotion(true);
            return;
        }

        const runtime: IAnimationRuntime = {
            frameId: 0,
            isInView: true,
            lastFrameTime: 0,
            motion: createPingPongMotion(),
            overflowPx: 0,
            prefersReducedMotion: prefersReducedMotion()
        };

        const applyOffset = () => {
            if (runtime.overflowPx <= 0 || runtime.prefersReducedMotion) {
                content.style.transform = "";
                return;
            }

            content.style.transform = `translate3d(${-runtime.motion.position}px, 0, 0)`;
        };

        const publishOverflowState = () => {
            setIsOverflowing(runtime.overflowPx > 0);
            setIsReducedMotion(runtime.prefersReducedMotion);
            setIsInView(runtime.isInView);
        };

        const stopLoop = () => {
            if (!runtime.frameId) {
                return;
            }

            cancelAnimationFrame(runtime.frameId);
            runtime.frameId = 0;
            runtime.lastFrameTime = 0;
        };

        const tick = (timestamp: number) => {
            if (document.hidden || runtime.prefersReducedMotion || runtime.overflowPx <= 0 || !runtime.isInView) {
                runtime.frameId = 0;
                runtime.lastFrameTime = 0;
                return;
            }

            runtime.frameId = requestAnimationFrame(tick);

            if (runtime.lastFrameTime === 0) {
                runtime.lastFrameTime = timestamp;
                return;
            }

            const deltaMs = timestamp - runtime.lastFrameTime;
            runtime.lastFrameTime = timestamp;
            runtime.motion = advancePingPong(runtime.motion, deltaMs, runtime.overflowPx);
            applyOffset();
        };

        const startLoop = () => {
            if (runtime.frameId || runtime.prefersReducedMotion || runtime.overflowPx <= 0 || !runtime.isInView) {
                return;
            }

            runtime.frameId = requestAnimationFrame(tick);
            applyOffset();
        };

        const measure = () => {
            runtime.overflowPx = getOverflowPx(content.scrollWidth, container.clientWidth);

            if (runtime.overflowPx <= 0 || runtime.prefersReducedMotion || !runtime.isInView) {
                runtime.motion = createPingPongMotion();
                content.style.transform = "";
                stopLoop();
            } else {
                startLoop();
            }

            publishOverflowState();
            applyOffset();
        };

        const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onMotionPreferenceChange = () => {
            runtime.prefersReducedMotion = motionQuery.matches;
            measure();
        };

        motionQuery.addEventListener("change", onMotionPreferenceChange);

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(container);
        resizeObserver.observe(content);

        const intersectionObserver = new IntersectionObserver(([entry]) => {
            runtime.isInView = entry?.isIntersecting ?? false;
            measure();
        });
        intersectionObserver.observe(container);

        const onVisibilityChange = () => {
            if (document.hidden) {
                runtime.lastFrameTime = 0;
                return;
            }

            startLoop();
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        measure();

        return () => {
            stopLoop();
            motionQuery.removeEventListener("change", onMotionPreferenceChange);
            resizeObserver.disconnect();
            intersectionObserver.disconnect();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            content.style.transform = "";
        };
    }, [enabled]);

    const showEllipsis = enabled && isOverflowing && (isReducedMotion || !isInView);

    return {
        containerRef,
        contentRef,
        isOverflowing,
        showEllipsis
    };
};
import {useEffect, useRef, useState} from "react";

export const PING_PONG_END_PAUSE_MS = 1200;
export const PING_PONG_SPEED_PX_PER_SEC = 32;
export const PING_PONG_START_DELAY_MS = 800;

interface IParams {
    enabled?: boolean;
}

export interface IPingPongMotion {
    direction: -1 | 1;
    position: number;
    remainingPauseMs: number;
}

interface IAnimationRuntime {
    frameId: number;
    isInView: boolean;
    lastFrameTime: number;
    motion: IPingPongMotion;
    overflowPx: number;
    prefersReducedMotion: boolean;
}

export const getOverflowPx = (scrollWidth: number, clientWidth: number) => Math.max(0, Math.round(scrollWidth - clientWidth));

export const createPingPongMotion = (): IPingPongMotion => ({
    direction: 1,
    position: 0,
    remainingPauseMs: PING_PONG_START_DELAY_MS
});

export const advancePingPong = (motion: IPingPongMotion, deltaMs: number, overflowPx: number): IPingPongMotion => {
    if (overflowPx <= 0) {
        return {direction: 1, position: 0, remainingPauseMs: 0};
    }

    const {direction} = motion;
    let {position, remainingPauseMs} = motion;
    let moveMs = deltaMs;

    if (remainingPauseMs > 0) {
        if (moveMs <= remainingPauseMs) {
            return {direction, position, remainingPauseMs: remainingPauseMs - moveMs};
        }

        moveMs -= remainingPauseMs;
        remainingPauseMs = 0;
    }

    position += (direction * PING_PONG_SPEED_PX_PER_SEC * moveMs) / 1000;

    if (position >= overflowPx) {
        return {direction: -1, position: overflowPx, remainingPauseMs: PING_PONG_END_PAUSE_MS};
    }

    if (position <= 0) {
        return {direction: 1, position: 0, remainingPauseMs: PING_PONG_END_PAUSE_MS};
    }

    return {direction, position, remainingPauseMs: 0};
};

const prefersReducedMotion = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const usePingPongOverflowText = (params?: IParams) => {
    const enabled = params?.enabled ?? true;
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [isReducedMotion, setIsReducedMotion] = useState(prefersReducedMotion);
    const [isInView, setIsInView] = useState(true);

    useEffect(() => {
        if (!enabled) {
            setIsOverflowing(false);
            return;
        }

        const container = containerRef.current;
        const content = contentRef.current;

        if (!container || !content) {
            return;
        }

        if (typeof ResizeObserver === "undefined" || typeof IntersectionObserver === "undefined") {
            setIsOverflowing(getOverflowPx(content.scrollWidth, container.clientWidth) > 0);
            setIsReducedMotion(true);
            return;
        }

        const runtime: IAnimationRuntime = {
            frameId: 0,
            isInView: true,
            lastFrameTime: 0,
            motion: createPingPongMotion(),
            overflowPx: 0,
            prefersReducedMotion: prefersReducedMotion()
        };

        const applyOffset = () => {
            if (runtime.overflowPx <= 0 || runtime.prefersReducedMotion) {
                content.style.transform = "";
                return;
            }

            content.style.transform = `translate3d(${-runtime.motion.position}px, 0, 0)`;
        };

        const publishOverflowState = () => {
            setIsOverflowing(runtime.overflowPx > 0);
            setIsReducedMotion(runtime.prefersReducedMotion);
            setIsInView(runtime.isInView);
        };

        const stopLoop = () => {
            if (!runtime.frameId) {
                return;
            }

            cancelAnimationFrame(runtime.frameId);
            runtime.frameId = 0;
            runtime.lastFrameTime = 0;
        };

        const tick = (timestamp: number) => {
            if (document.hidden || runtime.prefersReducedMotion || runtime.overflowPx <= 0 || !runtime.isInView) {
                runtime.frameId = 0;
                runtime.lastFrameTime = 0;
                return;
            }

            runtime.frameId = requestAnimationFrame(tick);

            if (runtime.lastFrameTime === 0) {
                runtime.lastFrameTime = timestamp;
                return;
            }

            const deltaMs = timestamp - runtime.lastFrameTime;
            runtime.lastFrameTime = timestamp;
            runtime.motion = advancePingPong(runtime.motion, deltaMs, runtime.overflowPx);
            applyOffset();
        };

        const startLoop = () => {
            if (runtime.frameId || runtime.prefersReducedMotion || runtime.overflowPx <= 0 || !runtime.isInView) {
                return;
            }

            runtime.frameId = requestAnimationFrame(tick);
            applyOffset();
        };

        const measure = () => {
            runtime.overflowPx = getOverflowPx(content.scrollWidth, container.clientWidth);

            if (runtime.overflowPx <= 0 || runtime.prefersReducedMotion || !runtime.isInView) {
                runtime.motion = createPingPongMotion();
                content.style.transform = "";
                stopLoop();
            } else {
                startLoop();
            }

            publishOverflowState();
            applyOffset();
        };

        const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onMotionPreferenceChange = () => {
            runtime.prefersReducedMotion = motionQuery.matches;
            measure();
        };

        motionQuery.addEventListener("change", onMotionPreferenceChange);

        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(container);
        resizeObserver.observe(content);

        const intersectionObserver = new IntersectionObserver(([entry]) => {
            runtime.isInView = entry?.isIntersecting ?? false;
            measure();
        });
        intersectionObserver.observe(container);

        const onVisibilityChange = () => {
            if (document.hidden) {
                runtime.lastFrameTime = 0;
                return;
            }

            startLoop();
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        measure();

        return () => {
            stopLoop();
            motionQuery.removeEventListener("change", onMotionPreferenceChange);
            resizeObserver.disconnect();
            intersectionObserver.disconnect();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            content.style.transform = "";
        };
    }, [enabled]);

    const showEllipsis = enabled && isOverflowing && (isReducedMotion || !isInView);

    return {
        containerRef,
        contentRef,
        isOverflowing,
        showEllipsis
    };
};
